import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { assessmentPortalGuard } from './core/guards/assessment-portal.guard';
import { assignedUserGuard } from './core/guards/assigned-user.guard';
import { roleGuard } from './core/guards/role.guard';
import { pendingChangesGuard } from './core/guards/pending-changes.guard';
import { helpRouteData } from './core/help/help-route-data';
import { pageTitle } from './core/branding/brand.config';
import {
  ERROR_PAGE_DATA_KEY,
  NOT_FOUND_ERROR,
  SERVER_ERROR,
  SERVICE_UNAVAILABLE_ERROR,
} from './features/errors/error-page.data';

export const routes: Routes = [
  // Public routes
  {
    path: 'login',
    title: pageTitle('Sign in'),
    loadComponent: () =>
      import('./features/auth/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'change-password',
    title: pageTitle('Change Password'),
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/auth/change-password/change-password-page.component').then(
        (m) => m.ChangePasswordPageComponent
      ),
  },

  // Protected routes — require authentication.
  // Note: mustChangePasswordGuard has been removed until BE support lands.
  // See core/guards/must-change-password.guard.ts for the TODO.
  {
    path: '',
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    children: [
      {
        path: 'dashboard',
        title: pageTitle('Dashboard'),
        canActivate: [assessmentPortalGuard],
        data: helpRouteData('dashboard'),
        loadComponent: () =>
          import('./features/dashboard/pages/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent
          ),
      },
      {
        path: 'activity',
        title: pageTitle('Activity Log'),
        canActivate: [assessmentPortalGuard],
        data: helpRouteData('dashboard'),
        loadComponent: () =>
          import('./features/dashboard/pages/activity/activity-page.component').then(
            (m) => m.ActivityPageComponent
          ),
      },
      {
        path: 'children',
        title: pageTitle('Children'),
        canActivate: [assessmentPortalGuard],
        data: helpRouteData('children-list'),
        loadComponent: () =>
          import('./features/children/pages/children-list-page/children-list-page.component').then(
            (m) => m.ChildrenListPageComponent
          ),
      },
      {
        path: 'children/new',
        title: pageTitle('Add Child'),
        canActivate: [assessmentPortalGuard, roleGuard(['SITEMANAGER'])],
        canDeactivate: [pendingChangesGuard],
        data: helpRouteData('child-form'),
        loadComponent: () =>
          import('./features/children/pages/child-form-page/child-form-page.component').then(
            (m) => m.ChildFormPageComponent
          ),
      },
      {
        path: 'children/:childId',
        title: pageTitle('Child Profile'),
        canActivate: [assessmentPortalGuard],
        data: helpRouteData('child-detail'),
        loadComponent: () =>
          import('./features/children/pages/child-detail-page/child-detail-page.component').then(
            (m) => m.ChildDetailPageComponent
          ),
      },
      {
        path: 'children/:childId/edit',
        title: pageTitle('Edit Child'),
        canActivate: [assessmentPortalGuard, roleGuard(['SITEMANAGER'])],
        canDeactivate: [pendingChangesGuard],
        data: helpRouteData('child-form'),
        loadComponent: () =>
          import('./features/children/pages/child-form-page/child-form-page.component').then(
            (m) => m.ChildFormPageComponent
          ),
      },
      {
        path: 'children/:childId/progress',
        title: pageTitle('Child Progress'),
        canActivate: [assessmentPortalGuard],
        data: helpRouteData('child-progress'),
        loadComponent: () =>
          import('./features/children/pages/progress-page/progress-page.component').then(
            (m) => m.ProgressPageComponent
          ),
      },
      {
        path: 'assessments',
        title: pageTitle('Assessments'),
        canActivate: [assessmentPortalGuard],
        data: helpRouteData('assessment-list'),
        loadComponent: () =>
          import('./features/assessments/pages/assessments-list-page/assessment-list-page.component').then(
            (m) => m.AssessmentListPageComponent
          ),
      },
      {
        path: 'assessments/new/:childId',
        title: pageTitle('New Assessment'),
        canActivate: [assessmentPortalGuard],
        canDeactivate: [pendingChangesGuard],
        data: helpRouteData('assessment-edit'),
        loadComponent: () =>
          import('./features/assessments/pages/assessments-edit-page/assessment-edit-page.component').then(
            (m) => m.AssessmentEditPageComponent
          ),
      },
      {
        path: 'assessments/:id/edit',
        title: pageTitle('Edit Assessment'),
        canActivate: [assessmentPortalGuard],
        canDeactivate: [pendingChangesGuard],
        data: helpRouteData('assessment-edit'),
        loadComponent: () =>
          import('./features/assessments/pages/assessments-edit-page/assessment-edit-page.component').then(
            (m) => m.AssessmentEditPageComponent
          ),
      },
      {
        path: 'assessments/:id/review',
        title: pageTitle('Review Assessment'),
        canActivate: [assessmentPortalGuard, roleGuard(['SITEMANAGER'])],
        canDeactivate: [pendingChangesGuard],
        data: helpRouteData('assessment-review'),
        loadComponent: () =>
          import('./features/assessments/pages/assessments-review-page/assessment-review-page.component').then(
            (m) => m.AssessmentReviewPageComponent
          ),
      },
      {
        path: 'assessments/:id',
        title: pageTitle('Assessment'),
        canActivate: [assessmentPortalGuard],
        data: helpRouteData('assessment-view'),
        loadComponent: () =>
          import('./features/assessments/pages/assessments-view-page/assessment-view-page.component').then(
            (m) => m.AssessmentViewPageComponent
          ),
      },
      {
        path: 'admin',
        title: pageTitle('Admin'),
        canActivate: [roleGuard(['SUPERUSER'])],
        loadComponent: () =>
          import('./features/admin/pages/admin-shell/admin-shell-page.component').then(
            (m) => m.AdminShellPageComponent
          ),
        children: [
          {
            path: '',
            redirectTo: 'site-managers',
            pathMatch: 'full',
          },
          {
            path: 'site-managers',
            title: pageTitle('Site Managers'),
            canActivate: [roleGuard(['SUPERUSER'])],
            data: helpRouteData('educators'),
            loadComponent: () =>
              import('./features/admin/pages/admin-site-managers/admin-site-managers-page.component').then(
                (m) => m.AdminSiteManagersPageComponent
              ),
          },
          {
            path: 'educators',
            title: pageTitle('Educators'),
            canActivate: [roleGuard(['SUPERUSER'])],
            data: helpRouteData('educators'),
            loadComponent: () =>
              import('./features/admin/pages/admin-educators/admin-educators-page.component').then(
                (m) => m.AdminEducatorsPageComponent
              ),
          },
          {
            path: 'settings',
            title: pageTitle('Settings'),
            canActivate: [roleGuard(['SUPERUSER'])],
            data: helpRouteData('settings'),
            loadComponent: () =>
              import('./features/admin/pages/admin-settings/admin-settings-page.component').then(
                (m) => m.AdminSettingsPageComponent
              ),
          },
          {
            path: 'settings/new',
            title: pageTitle('Add Setting'),
            canActivate: [roleGuard(['SUPERUSER'])],
            canDeactivate: [pendingChangesGuard],
            data: helpRouteData('settings'),
            loadComponent: () =>
              import('./features/settings/pages/setting-form/setting-form-page.component').then(
                (m) => m.SettingFormPageComponent
              ),
          },
          {
            path: 'settings/:settingId/edit',
            title: pageTitle('Edit Setting'),
            canActivate: [roleGuard(['SUPERUSER'])],
            canDeactivate: [pendingChangesGuard],
            data: helpRouteData('settings'),
            loadComponent: () =>
              import('./features/settings/pages/setting-form/setting-form-page.component').then(
                (m) => m.SettingFormPageComponent
              ),
          },
          {
            path: 'staff/new',
            title: pageTitle('Add Staff'),
            canActivate: [roleGuard(['SUPERUSER'])],
            data: helpRouteData('educators'),
            loadComponent: () =>
              import('./features/educators/pages/educator-form/educator-form-page.component').then(
                (m) => m.EducatorFormPageComponent
              ),
          },
          {
            path: 'staff/:userId/edit',
            title: pageTitle('Edit Staff'),
            canActivate: [roleGuard(['SUPERUSER'])],
            data: helpRouteData('educators'),
            loadComponent: () =>
              import('./features/educators/pages/educator-form/educator-form-page.component').then(
                (m) => m.EducatorFormPageComponent
              ),
          },
        ],
      },
      // Educators (Site Manager workflow)
      {
        path: 'educators',
        title: pageTitle('Educators'),
        canActivate: [assessmentPortalGuard, roleGuard(['SITEMANAGER'])],
        data: helpRouteData('educators'),
        loadComponent: () =>
          import('./features/educators/pages/educators-list/educators-list-page.component').then(
            (m) => m.EducatorsListPageComponent
          ),
      },
      {
        path: 'educators/new',
        title: pageTitle('Add Educator'),
        canActivate: [assessmentPortalGuard, roleGuard(['SITEMANAGER'])],
        data: helpRouteData('educators'),
        loadComponent: () =>
          import('./features/educators/pages/educator-form/educator-form-page.component').then(
            (m) => m.EducatorFormPageComponent
          ),
      },
      // Settings & Profile
      {
        path: 'profile',
        title: pageTitle('Profile'),
        data: helpRouteData('profile'),
        loadComponent: () =>
          import('./features/settings/pages/profile/profile-page.component').then(
            (m) => m.ProfilePageComponent
          ),
      },
      {
        path: 'settings/new',
        title: pageTitle('Add Setting'),
        canActivate: [roleGuard(['SUPERUSER'])],
        canDeactivate: [pendingChangesGuard],
        data: helpRouteData('settings'),
        loadComponent: () =>
          import('./features/settings/pages/setting-form/setting-form-page.component').then(
            (m) => m.SettingFormPageComponent
          ),
      },
      {
        path: 'settings',
        title: pageTitle('Settings'),
        canActivate: [assessmentPortalGuard, roleGuard(['SITEMANAGER'])],
        canDeactivate: [pendingChangesGuard],
        data: helpRouteData('settings'),
        loadComponent: () =>
          import('./features/settings/pages/settings/settings-page.component').then(
            (m) => m.SettingsPageComponent
          ),
      },
      {
        path: 'docs',
        canActivate: [assignedUserGuard],
        canActivateChild: [assignedUserGuard],
        children: [
          {
            path: '',
            redirectTo: 'getting-started',
            pathMatch: 'full',
          },
          {
            path: ':slug',
            title: pageTitle('Docs'),
            loadComponent: () =>
              import('./features/docs/pages/docs-page/docs-page.component').then(
                (m) => m.DocsPageComponent
              ),
          },
        ],
      },
      {
        path: 'help',
        title: pageTitle('Help'),
        loadComponent: () =>
          import('./features/settings/pages/help/help-page.component').then(
            (m) => m.HelpPageComponent
          ),
      },
      {
        path: 'about',
        title: pageTitle('About'),
        loadComponent: () =>
          import('./features/settings/pages/about/about-page.component').then(
            (m) => m.AboutPageComponent
          ),
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
    ],
  },
  // Public error routes — reachable by authenticated and unauthenticated users.
  {
    path: 'error/500',
    title: pageTitle('Something went wrong'),
    data: { [ERROR_PAGE_DATA_KEY]: SERVER_ERROR },
    loadComponent: () =>
      import('./features/errors/pages/error-page.component').then((m) => m.ErrorPageComponent),
  },
  {
    path: 'error/unavailable',
    title: pageTitle('Temporarily unavailable'),
    data: { [ERROR_PAGE_DATA_KEY]: SERVICE_UNAVAILABLE_ERROR },
    loadComponent: () =>
      import('./features/errors/pages/error-page.component').then((m) => m.ErrorPageComponent),
  },
  {
    path: '**',
    title: pageTitle('Page Not Found'),
    data: { [ERROR_PAGE_DATA_KEY]: NOT_FOUND_ERROR },
    loadComponent: () =>
      import('./features/errors/pages/error-page.component').then((m) => m.ErrorPageComponent),
  },
];
